import { useState, useEffect } from 'react';
import { Save, Users, Calendar, BookOpen, UserCheck, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import api, { classApi, studentApi, teacherApi } from '../lib/api';

// Grades options
const GRADES = [
  { value: 'weak', label_ar: 'ضعيف', label_fr: 'Faible', color: 'bg-red-100 text-red-700' },
  { value: 'acceptable', label_ar: 'مقبول', label_fr: 'Acceptable', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'good', label_ar: 'جيد', label_fr: 'Bien', color: 'bg-blue-100 text-blue-700' },
  { value: 'very_good', label_ar: 'جيد جدا', label_fr: 'Très bien', color: 'bg-green-100 text-green-700' },
];

// Trimestres
const TRIMESTERS = [
  { value: 1, label: 'Trimestre 1 (Sept-Nov)' },
  { value: 2, label: 'Trimestre 2 (Déc-Fév)' },
  { value: 3, label: 'Trimestre 3 (Mars-Juin)' },
];

export default function TrimestrialEvalPage() {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedTrimester, setSelectedTrimester] = useState(1);
  const [selectedYear, setSelectedYear] = useState('2024-2025');
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [evaluation, setEvaluation] = useState({
    parts: Array.from({ length: 12 }, (_, i) => ({ part_number: i + 1, surah_name: '', grade: null })),
    tajweed_grade: null,
    curriculum_grade: null,
    morning_attendance: 0,
    evening_attendance: 0,
    lesson_amount: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedStudent && selectedTrimester && selectedYear) {
      fetchExistingEval();
    }
  }, [selectedStudent, selectedTrimester, selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [classesRes, studentsRes, teachersRes] = await Promise.all([
        classApi.getAll({}),
        studentApi.getAll({ is_active: true }),
        teacherApi.getAll({ is_active: true }),
      ]);
      setClasses(classesRes.data);
      setStudents(studentsRes.data);
      setTeachers(teachersRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingEval = async () => {
    try {
      const response = await api.get(`/trimestrial-evals?student_id=${selectedStudent}&trimester=${selectedTrimester}&school_year=${selectedYear}`);
      if (response.data.length > 0) {
        const existing = response.data[0];
        setEvaluation({
          parts: existing.parts || Array.from({ length: 12 }, (_, i) => ({ part_number: i + 1, surah_name: '', grade: null })),
          tajweed_grade: existing.tajweed_grade,
          curriculum_grade: existing.curriculum_grade,
          morning_attendance: existing.morning_attendance || 0,
          evening_attendance: existing.evening_attendance || 0,
          lesson_amount: existing.lesson_amount || '',
        });
        if (existing.teacher_id) {
          setSelectedTeacher(existing.teacher_id);
        }
      } else {
        // Reset evaluation
        setEvaluation({
          parts: Array.from({ length: 12 }, (_, i) => ({ part_number: i + 1, surah_name: '', grade: null })),
          tajweed_grade: null,
          curriculum_grade: null,
          morning_attendance: 0,
          evening_attendance: 0,
          lesson_amount: '',
        });
      }
    } catch (error) {
      console.error('Erreur chargement évaluation:', error);
    }
  };

  const getClassStudents = () => {
    return students.filter(s => s.class_id === selectedClass);
  };

  const getStudentName = (studentId) => {
    const student = students.find(s => s.id === studentId);
    return student ? `${student.first_name} ${student.last_name}` : '';
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher ? `${teacher.first_name} ${teacher.last_name}` : '';
  };

  const updatePart = (partNumber, field, value) => {
    setEvaluation(prev => ({
      ...prev,
      parts: prev.parts.map(p =>
        p.part_number === partNumber ? { ...p, [field]: value } : p
      )
    }));
  };

  const handleSave = async () => {
    if (!selectedStudent || !selectedTeacher) {
      toast.error('Veuillez sélectionner un élève et un enseignant');
      return;
    }

    const student = students.find(s => s.id === selectedStudent);

    setSaving(true);
    try {
      await api.post('/trimestrial-evals', {
        student_id: selectedStudent,
        class_id: student?.class_id || selectedClass,
        teacher_id: selectedTeacher,
        trimester: selectedTrimester,
        school_year: selectedYear,
        parts: evaluation.parts,
        tajweed_grade: evaluation.tajweed_grade,
        curriculum_grade: evaluation.curriculum_grade,
        morning_attendance: evaluation.morning_attendance,
        evening_attendance: evaluation.evening_attendance,
        lesson_amount: evaluation.lesson_amount,
      });
      toast.success('Évaluation enregistrée');
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const classStudents = getClassStudents();
  const currentYear = new Date().getFullYear();
  const schoolYears = [
    `${currentYear - 1}-${currentYear}`,
    `${currentYear}-${currentYear + 1}`,
  ];

  if (loading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="trimestrial-eval-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">EVALUATION DES ELEVES CORANIQUES</h1>
          <p className="text-muted-foreground text-sm">Évaluation Trimestrielle - Par élève</p>
        </div>
        <Button
          className="rounded-full"
          onClick={handleSave}
          disabled={saving || !selectedStudent || !selectedTeacher}
          data-testid="save-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>

      {/* Filtres */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>الفصل (Classe)</Label>
              <Select value={selectedClass} onValueChange={(v) => { setSelectedClass(v); setSelectedStudent(''); }}>
                <SelectTrigger data-testid="select-class">
                  <SelectValue placeholder="Classe" />
                </SelectTrigger>
                <SelectContent>
                  {classes.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>التلميذ (Élève)</Label>
              <Select value={selectedStudent} onValueChange={setSelectedStudent} disabled={!selectedClass}>
                <SelectTrigger data-testid="select-student">
                  <SelectValue placeholder="Élève" />
                </SelectTrigger>
                <SelectContent>
                  {classStudents.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>المعلم (Enseignant)</Label>
              <Select value={selectedTeacher} onValueChange={setSelectedTeacher}>
                <SelectTrigger data-testid="select-teacher">
                  <SelectValue placeholder="Enseignant" />
                </SelectTrigger>
                <SelectContent>
                  {teachers.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Trimestre</Label>
              <Select value={selectedTrimester.toString()} onValueChange={(v) => setSelectedTrimester(parseInt(v))}>
                <SelectTrigger data-testid="select-trimester">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRIMESTERS.map(t => (
                    <SelectItem key={t.value} value={t.value.toString()}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Année scolaire</Label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger data-testid="select-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {schoolYears.map(y => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tableau d'évaluation par partie */}
      {selectedStudent ? (
        <>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <BookOpen className="w-5 h-5" />
                {getStudentName(selectedStudent)} - {TRIMESTERS.find(t => t.value === selectedTrimester)?.label}
              </CardTitle>
              <CardDescription>
                Évaluez chaque partie (1-12) avec la sourate et la note correspondante
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-[80px] text-center">PARTIE</TableHead>
                      <TableHead className="min-w-[180px]">السور (Sourate)</TableHead>
                      <TableHead className="text-center">ضعيف<br/><span className="text-xs">Faible</span></TableHead>
                      <TableHead className="text-center">مقبول<br/><span className="text-xs">Acceptable</span></TableHead>
                      <TableHead className="text-center">جيد<br/><span className="text-xs">Bien</span></TableHead>
                      <TableHead className="text-center">جيد جدا<br/><span className="text-xs">Très bien</span></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {evaluation.parts.map((part) => (
                      <TableRow key={part.part_number}>
                        <TableCell className="font-bold text-center">{part.part_number}</TableCell>
                        <TableCell>
                          <Input
                            value={part.surah_name || ''}
                            onChange={(e) => updatePart(part.part_number, 'surah_name', e.target.value)}
                            placeholder="Nom de la sourate"
                            className="h-9 text-right"
                            dir="rtl"
                          />
                        </TableCell>
                        {GRADES.map(grade => (
                          <TableCell key={grade.value} className="text-center">
                            <Button
                              variant={part.grade === grade.value ? 'default' : 'outline'}
                              size="sm"
                              className={`w-8 h-8 p-0 ${part.grade === grade.value ? grade.color : ''}`}
                              onClick={() => updatePart(part.part_number, 'grade', grade.value)}
                            >
                              {part.grade === grade.value && <CheckCircle2 className="w-4 h-4" />}
                            </Button>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/30 font-bold">
                      <TableCell colSpan={2} className="text-center">TOTAL</TableCell>
                      <TableCell className="text-center">{evaluation.parts.filter(p => p.grade === 'weak').length}</TableCell>
                      <TableCell className="text-center">{evaluation.parts.filter(p => p.grade === 'acceptable').length}</TableCell>
                      <TableCell className="text-center">{evaluation.parts.filter(p => p.grade === 'good').length}</TableCell>
                      <TableCell className="text-center">{evaluation.parts.filter(p => p.grade === 'very_good').length}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Appréciation pour l'enseignant */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">APPRECIATION POUR L'ENSEIGNANT</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* التجويد - Tajweed */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">التجويد (Tajweed)</Label>
                  <div className="flex flex-wrap gap-2">
                    {GRADES.map(grade => (
                      <Button
                        key={grade.value}
                        variant={evaluation.tajweed_grade === grade.value ? 'default' : 'outline'}
                        size="sm"
                        className={evaluation.tajweed_grade === grade.value ? grade.color : ''}
                        onClick={() => setEvaluation(prev => ({ ...prev, tajweed_grade: grade.value }))}
                      >
                        {grade.label_ar} ({grade.label_fr})
                      </Button>
                    ))}
                  </div>
                </div>

                {/* المنهج - Curriculum */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">المنهج (Curriculum)</Label>
                  <div className="flex flex-wrap gap-2">
                    {GRADES.map(grade => (
                      <Button
                        key={grade.value}
                        variant={evaluation.curriculum_grade === grade.value ? 'default' : 'outline'}
                        size="sm"
                        className={evaluation.curriculum_grade === grade.value ? grade.color : ''}
                        onClick={() => setEvaluation(prev => ({ ...prev, curriculum_grade: grade.value }))}
                      >
                        {grade.label_ar} ({grade.label_fr})
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label>م صباحية (Présence matin)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={evaluation.morning_attendance}
                    onChange={(e) => setEvaluation(prev => ({ ...prev, morning_attendance: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>م مسائية (Présence soir)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={evaluation.evening_attendance}
                    onChange={(e) => setEvaluation(prev => ({ ...prev, evening_attendance: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>مقدار الدرس (Quantité leçon)</Label>
                  <Input
                    value={evaluation.lesson_amount}
                    onChange={(e) => setEvaluation(prev => ({ ...prev, lesson_amount: e.target.value }))}
                    placeholder="Ex: 2 pages"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium text-lg">Sélectionnez un élève</h3>
            <p className="text-muted-foreground">Choisissez d'abord une classe, puis un élève pour saisir son évaluation trimestrielle</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
